import { api, Api } from '@/services/api';
import type {
  CreateProfileParams,
  Game,
  ListResult,
  Platforms,
  Profile,
} from '@/types';

const gamesEndpoints = api.injectEndpoints({
  endpoints: (builder) => ({
    games: builder.query<Game[], void>({
      query: () => ({ url: Api.Games }),
      providesTags: ['Games'],
    }),
    platforms: builder.query<Platforms[], void>({
      query: () => ({ url: Api.Platforms }),
    }),
    favoriteGames: builder.query<Game[], number>({
      query: (id) => ({ url: Api.FavoriteGames.replace(':id', id.toString()) }),
    }),
    profiles: builder.query<ListResult<Profile>, number>({
      query: (id) => ({ url: Api.Profiles.replace(':id', id.toString()) }),
      providesTags: ['Profiles'],
    }),
    profileId: builder.query<Profile, { id: number; profileId: number }>({
      query: (params) => ({
        url: Api.ProfilesId.replace(':id', params.id.toString()).replace(
          ':profileId',
          params.profileId.toString(),
        ),
      }),
    }),
    /**
     * Upsert a game profile on the Laravel monolith via the bulk endpoint
     * (`PATCH /users/me/profiles`, body `{ data: [profile] }`). This is what
     * makes Clash Royale tournament join work: Laravel finds the existing
     * (user, game_id, platform_id) profile and RE-VALIDATES + refreshes the
     * stored in-game API token (info.id = "#TAG-APITOKEN") — or creates it if
     * absent. We use bulk (not POST store) precisely so re-verifying refreshes
     * the rotated token in place instead of creating a duplicate profile.
     */
    upsertProfile: builder.mutation<unknown, CreateProfileParams>({
      query: (profile) => ({
        url: Api.CreateProfile,
        method: 'PATCH',
        body: { data: [profile] },
      }),
      invalidatesTags: ['Profiles'],
    }),
  }),
});

export const {
  useGamesQuery,
  useLazyGamesQuery,
  usePlatformsQuery,
  useFavoriteGamesQuery,
  useProfilesQuery,
  useProfileIdQuery,
  useUpsertProfileMutation,
} = gamesEndpoints;
